/**
 * Owner-facing Generate / Improve CTAs.
 * URL-analyze auto-saves selectedKeyword, which used to flip readOnly and hide
 * Generovat under the analysis tables. The brief card then only showed Improve
 * for isAnalyze — so writing + analyze both lost a visible write button.
 *
 * Later: selectedKeyword on a new article (failed first generate, or the
 * auto-save) still flipped readOnly while the H1/H2 body was empty. Keep
 * Generate visible until Lucy actually wrote body copy.
 */

export function articleHasGeneratedBody(article?: {
    h1?: Array<{h?: string; p?: string}> | null;
    h2?: Array<{h?: string; p?: string}> | null;
} | null): boolean {
    const textOf = (value?: string) => String(value || '').replace(/<[^>]+>/g, ' ').trim();
    if (textOf(article?.h1?.[0]?.p).length >= 24) {
        return true;
    }
    return (article?.h2 || []).some((block) => textOf(block?.p).length >= 24);
}

export function keywordWorkspaceWriteSlotsReady(lucySlotCount: number): boolean {
    return lucySlotCount >= LUCY_MIN_GENERATE_SLOTS && lucySlotCount <= LUCY_MAX_GENERATE_SLOTS;
}

/**
 * Finished whole-article generate: manual enhance again from keyword analysis.
 * OpenAIArticleGenerate writes every non-URL-improve article whole, so this
 * replaces the text. A v1.14.2 article is not offered it (#31025).
 */
export function isManualReEnhanceArticle(opts: {
    isAnalyze: boolean;
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
}): boolean {
    return !opts.isAnalyze && (opts.enhanced === 'done' || opts.aiGenerateStatus === 'done');
}

/**
 * Generate / Vylepšit článek in the keyword workspace (brief header + top CTA).
 * SEO-score entry opens analysis with keywords selected while the Lucy fork or
 * a settled article used to hide the button even though slots were ready.
 */
export function shouldShowKeywordWorkspaceWriteCta(opts: {
    sharePreview: boolean;
    isAnalyze: boolean;
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
    readOnly: boolean;
    selectedKeyword?: string;
    hasGeneratedBody?: boolean;
    showLucyFork: boolean;
    keywordSelectionShown: boolean;
    lucySlotCount: number;
}): boolean {
    if (opts.sharePreview) {
        return false;
    }
    const analysisOpenWithSlots = opts.keywordSelectionShown
        && keywordWorkspaceWriteSlotsReady(opts.lucySlotCount);
    if (analysisOpenWithSlots && opts.enhanced !== 'processing') {
        if (opts.isAnalyze || isManualReEnhanceArticle(opts)) {
            return true;
        }
    }
    if (opts.showLucyFork) {
        return false;
    }
    return shouldShowArticleWriteCta({
        sharePreview: opts.sharePreview,
        isAnalyze: opts.isAnalyze,
        enhanced: opts.enhanced,
        aiGenerateStatus: opts.aiGenerateStatus,
        readOnly: opts.readOnly,
        selectedKeyword: opts.selectedKeyword,
        hasGeneratedBody: opts.hasGeneratedBody,
    });
}

export function shouldShowArticleWriteCta(opts: {
    sharePreview: boolean;
    isAnalyze: boolean;
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
    readOnly: boolean;
    selectedKeyword?: string;
    hasGeneratedBody?: boolean;
}): boolean {
    if (opts.sharePreview) {
        return false;
    }
    // A finished article keeps this button after refresh when the body check
    // misses. There is no separate "generate again" action, so hide it.
    if (opts.enhanced === 'done' || opts.aiGenerateStatus === 'done' || opts.aiGenerateStatus === 'review') {
        return false;
    }
    // A v1.14.2 article stays locked as v1.14.2 left it: any generate would
    // replace its text (#31025). The brief panel passes readOnly false.
    if (isLegacyWrittenArticle(opts)) {
        return false;
    }
    if (opts.isAnalyze) {
        return true;
    }
    if (opts.hasGeneratedBody) {
        return !opts.readOnly;
    }
    return true;
}

export type LucyWritePath = null | 'immediate' | 'customize-first';

/**
 * v1.14.2 wrote neither enhanced nor aiGenerateStatus, so its articles load as
 * enhanced pending with no status (#31025). Body text with no run status is
 * such an article. URL-improve is not: its scraped page text sits in H1/H2
 * before the first run.
 */
export function isLegacyWrittenArticle(opts: {
    aiGenerateStatus?: string | null;
    isAnalyze?: boolean | null;
    hasGeneratedBody?: boolean;
}): boolean {
    return !opts.isAnalyze && !opts.aiGenerateStatus && !!opts.hasGeneratedBody;
}

/**
 * A finished article is not a new brief. enhanced or aiGenerateStatus done
 * is enough: lucyWritePath is only local state and is null after refresh.
 * A v1.14.2 article is not one either: Generate now would replace its text.
 */
export function articleGenerateChoiceSettled(opts: {
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
    isAnalyze?: boolean | null;
    hasGeneratedBody?: boolean;
}): boolean {
    if (opts.enhanced === 'done' || opts.aiGenerateStatus === 'done') {
        return true;
    }
    return isLegacyWrittenArticle(opts);
}

/** Chat review passes the choice as ?cesta=immediate|customize-first. */
export function readChatWritePath(value: unknown): Exclude<LucyWritePath, null> | null {
    const raw = Array.isArray(value) ? value[0] : value;
    if (raw === 'immediate' || raw === 'customize-first') {
        return raw;
    }
    return null;
}

/**
 * Generate now / Edit before generating, and the URL-improve pair.
 * The choice stays up while the user is still choosing. A fresh keyword
 * article starts with empty inspirations, and a finished load below the old
 * 4-related / 2-question bar used to unmount the fork for the whole visit.
 * A finished article must not look new just because lucyWritePath is null.
 * URL-improve and any article still waiting for a choice keep the panel.
 * Review keeps Accept / Keep. A failed URL-improve keeps Retry.
 */
export function shouldShowLucyGenerateFork(opts: {
    disableSelection: boolean;
    lucyWritePath: LucyWritePath;
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
    isAnalyze?: boolean | null;
    hasGeneratedBody?: boolean;
}): boolean {
    if (opts.disableSelection || opts.lucyWritePath !== null) {
        return false;
    }
    if (articleGenerateChoiceSettled(opts)) {
        return false;
    }
    // Review is the Accept / Keep screen. A failed URL-improve shows Retry.
    if (opts.aiGenerateStatus === 'review') {
        return false;
    }
    if (opts.isAnalyze && opts.aiGenerateStatus === 'failed') {
        return false;
    }
    return true;
}

export function articleWriteCtaDisabled(opts: {
    selectedKeyword: string;
    tooManyKeywords: boolean;
    selectNewKeyword: boolean;
    lucySlotCount?: number;
}): boolean {
    if (opts.selectNewKeyword || !opts.selectedKeyword) {
        return true;
    }
    if (typeof opts.lucySlotCount === 'number') {
        return opts.lucySlotCount === 0 || opts.lucySlotCount > 20;
    }
    return opts.tooManyKeywords;
}

/** Same floor/ceiling as the Generate SEO article button. */
export const LUCY_MIN_GENERATE_SLOTS = 1;
export const LUCY_MAX_GENERATE_SLOTS = 20;

type HeadingBlock = {h?: string | null} | null | undefined;

export type ScrapedHeadingArticle = {
    url?: string | null;
    isAnalyze?: boolean | null;
    h1?: HeadingBlock[] | null;
    h2?: HeadingBlock[] | null;
    h3?: HeadingBlock[] | null;
    h4?: HeadingBlock[] | null;
    h5?: HeadingBlock[] | null;
    h6?: HeadingBlock[] | null;
};

function headingText(rows?: HeadingBlock[] | null): boolean {
    return Array.isArray(rows) && rows.some((row) => String(row?.h || '').trim().length > 0);
}

/** Scraped H1–H6 already written onto the article (in memory or on the saved doc). */
export function articleHasScrapedHeadings(article?: ScrapedHeadingArticle | null): boolean {
    if (!article) {
        return false;
    }
    return headingText(article.h1)
        || headingText(article.h2)
        || headingText(article.h3)
        || headingText(article.h4)
        || headingText(article.h5)
        || headingText(article.h6);
}

/** URL-improve copy: a source URL plus headings taken from that page. */
export function articleHasUrlAndScrapedHeadings(article?: ScrapedHeadingArticle | null): boolean {
    return String(article?.url || '').trim().length > 0 && articleHasScrapedHeadings(article);
}

/**
 * URL-improve Enhance stays off while CONTENT EXTRACTION is still running.
 * A click in that window used to enqueue parts before the page headings existed.
 */
export function enhanceWaitingForExtraction(opts: {
    isAnalyze: boolean;
    scrapeLoading: boolean;
}): boolean {
    return !!opts.isAnalyze && !!opts.scrapeLoading;
}

/**
 * Generate now / Improve now.
 * Disabled with the existing loader until every analysis fetch generation
 * reads has finished:
 * - keyword analysis (D4SGetInspiration, the paragraphs 0/20 counter)
 * - the AI writing brief, fast then full (D4SGetAiWritingBrief: intent,
 *   answer shape, and the questions / headings / entities the outline uses)
 * URL-improve also waits until GetScrapped has finished and the scraped
 * headings are on the article, so an early click cannot enqueue parts with
 * empty existingSectionHeadings. A click while this is false is ignored.
 */
export function isLucyForkGenerateReady(opts: {
    analysisLoading: boolean;
    briefLoading: boolean;
    isAnalyze: boolean;
    scrapeLoading: boolean;
    scrapedHeadingsSaved: boolean;
}): boolean {
    if (opts.analysisLoading || opts.briefLoading) {
        return false;
    }
    if (opts.isAnalyze && (opts.scrapeLoading || !opts.scrapedHeadingsSaved)) {
        return false;
    }
    return true;
}

export type KeywordSaveBranch = 'analyze' | 'whole';

/**
 * saveSelectedKeyword must not persist isAnalyze:false or aiGenerateMode whole
 * when the saved document is an analyze article, or the article has a URL plus
 * scraped headings. fromObject turns a missing flag into false; the next full
 * save would otherwise store that default.
 */
export function shouldPreserveAnalyzeOnKeywordSave(opts: {
    inMemoryIsAnalyze: boolean;
    savedIsAnalyze?: boolean | null;
    inMemoryArticle?: ScrapedHeadingArticle | null;
    savedArticle?: ScrapedHeadingArticle | null;
}): boolean {
    if (opts.inMemoryIsAnalyze || opts.savedIsAnalyze || opts.savedArticle?.isAnalyze) {
        return true;
    }
    return articleHasUrlAndScrapedHeadings(opts.inMemoryArticle)
        || articleHasUrlAndScrapedHeadings(opts.savedArticle);
}

export async function resolveKeywordSaveBranch(opts: {
    inMemoryIsAnalyze: boolean;
    inMemoryArticle?: ScrapedHeadingArticle | null;
    readSaved?: () => Promise<ScrapedHeadingArticle | null | undefined>;
}): Promise<KeywordSaveBranch> {
    if (shouldPreserveAnalyzeOnKeywordSave({
        inMemoryIsAnalyze: opts.inMemoryIsAnalyze,
        inMemoryArticle: opts.inMemoryArticle,
    })) {
        return 'analyze';
    }
    if (!opts.readSaved) {
        return 'whole';
    }
    const saved = await opts.readSaved();
    if (shouldPreserveAnalyzeOnKeywordSave({
        inMemoryIsAnalyze: false,
        inMemoryArticle: opts.inMemoryArticle,
        savedArticle: saved,
        savedIsAnalyze: !!saved?.isAnalyze,
    })) {
        return 'analyze';
    }
    return 'whole';
}

export type LucyGenerateNowAction = 'start' | 'too-few' | 'too-many';

/**
 * Generate now / Improve now. Two selected paragraphs are enough to start
 * (QA article 5rKs7QltBK6mwYIqf6IT showed 2). There is no higher minimum in
 * the outline builder. Zero slots must not switch the panel. Above 20 matches
 * the disabled Generate SEO article button.
 */
export function lucyGenerateNowAction(
    lucySlotCount: number,
    maxSlots = LUCY_MAX_GENERATE_SLOTS
): LucyGenerateNowAction {
    if (lucySlotCount < LUCY_MIN_GENERATE_SLOTS) {
        return 'too-few';
    }
    if (lucySlotCount > maxSlots) {
        return 'too-many';
    }
    return 'start';
}
